import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useState, useEffect, useRef, useCallback } from 'react';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { FaPlus, FaSortUp, FaSortDown } from 'react-icons/fa6';
import { FiEye } from 'react-icons/fi';
import { MdFlag, MdOutlineFileDownload, MdOutlineFlag } from 'react-icons/md';
import { FaTrash } from 'react-icons/fa';
import { Pagination } from '~/components/Pagination';
import { formatCurrency } from '~/common/form/Inputs';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    defaultFundRequestFilters,
    deleteFundRequest,
    fetchFinancialCoordinators,
    fetchFundRequests,
    fetchFundRequestStatusCounts,
    fetchPatients,
    fetchQueue,
    fetchVendors,
} from '~/services/fund-requests';
import { FundRequestFilters, QueueResponse } from '~/types/fundrequests';
import {
    FundRequest,
    SearchVendorType,
    Option,
    SearchFinancialCoordinatorType,
} from '../expenses.types';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { SelectInstance, SingleValue } from 'react-select';
import moment from 'moment';
import AddFundRequestDrawer from '~/components/FundRequests/drawers/AddFundRequestDrawer';
import { useQuery } from '@tanstack/react-query';
import FundRequestStatusTabs from '../FundRequestStatusTabs';
import { SelectOption } from '~/types/globals';
import Panel from '~/components/Panel';
import Tabs from '~/components/Tabs';
import { QueueTable } from './QueueTable';
import { useAuth } from '~/context/AuthProvider';
import DeleteFundRequestModal from '~/components/FundRequests/modals/DeleteFundRequestModal';
import { colorStyles } from '~/common/SelectStyles';

const statusTabs = [
    { label: 'Open', status: 'open' },
    { label: 'Submitted', status: 'submitted' },
    { label: 'In Review', status: 'in_review' },
    { label: 'Needs Info', status: 'needs_info' },
    { label: 'AP Review', status: 'ap_review' },
    { label: 'Resubmitted', status: 'resubmitted' },
    { label: 'For DoF Approval', status: 'for_dof_approval' },
    { label: 'Appealed', status: 'appealed' },
    { label: 'Admin Draft', status: 'admin_draft' },
];

// Define status style mappings
const statusStyleMap: Record<string, string> = {
    paid: 'bg-primary',
    processing: 'bg-[#AFC12D]',
    pending: 'bg-[#F47D29]',
    failed: 'bg-danger text-white',
    submitted: 'bg-[#E5F7F7]',
    in_review: 'bg-primary',
    needs_info: 'bg-[#952E46] text-white',
    ap_review: 'bg-[#FEBF3E]',
    for_dof_approval: 'bg-[#F78154]',
    appealed: 'bg-black text-white',
    rejected: 'bg-danger text-white',
    approved: 'bg-[#AFC12D]',
    resubmitted: 'bg-[#97D7E1]',
    admin_draft: 'bg-[#6B7280] text-white',
    // Default style
    default: 'bg-[#97D7E1]',
};

// Function to get status style class
const getStatusStyle = (statusValue: string): string => {
    return statusStyleMap[statusValue] || statusStyleMap.default;
};

const sortByMap: Record<string, string> = {
    vendor: 'vendor_name',
    request_amount: 'amount',
    financial_coordinator: 'coordinator',
    fund_request_status: 'status',
    id: 'id',
    priority: 'priority',
    unread_notes: 'unread_notes',
    updated_date: 'updated_date',
    submitted_date: 'submitted_date',
};

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

const generatePageName = (status: string) => {
    switch (status) {
        case 'open':
            return 'All Open Fund Requests';
        case 'submitted':
            return 'Submitted Fund Requests';
        case 'in_review':
            return 'In Review Fund Requests';
        case 'needs_info':
            return 'Needs Info Fund Requests';
        case 'ap_review':
            return 'AP Review Fund Requests';
        case 'resubmitted':
            return 'Resubmitted Fund Requests';
        case 'for_dof_approval':
            return 'For DoF Approval Fund Requests';
        case 'appealed':
            return 'Appealed Fund Requests';
        case 'paid':
            return 'Paid Fund Requests';
        case 'rejected':
            return 'Rejected Fund Requests';
        case 'approved':
            return 'Approved Fund Requests';
        case 'admin_draft':
            return 'Admin Draft Fund Requests';
        default:
            return 'All Fund Requests';
    }
};

// Utility function to shorten names
const shortenName = (name: string): string => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0][0]}. ${parts[parts.length - 1]}`;
};

const FundRequests: React.FC = () => {
    const { person, user, refreshUser } = useAuth();
    const [pageSize] = useState(20);
    const [pages, setPages] = useState<number>(1);
    const [page, setPage] = useState<number>(1);
    const [results, setResults] = useState<FundRequest[]>([]);
    const vendorRef = useRef<SelectInstance<Option, false> | null>(null);
    const coordinatorRef = useRef<SelectInstance<SelectOption, false> | null>(
        null,
    );
    const [filters, setFilters] = useState<FundRequestFilters>(
        defaultFundRequestFilters,
    );
    const [financialCoordinators, setFinancialCoordinators] = useState<
        SelectOption[]
    >([]);
    const [selectedCoordinator, setSelectedCoordinator] =
        useState<SelectOption | null>(null);
    const [statusCounts, setStatusCounts] = useState<Record<
        string,
        number
    > | null>(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [status, setStatus] = useState<string>('open');
    const [isCreateFundRequestShowing, setIsCreatedFundRequestShowing] =
        useState(false);
    const [openTab, setOpenTab] = useState<number>(1);
    const [queue, setQueue] = useState<QueueResponse | null>();

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [fundRequestToDelete, setFundRequestToDelete] = useState<
        number | null
    >(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Add state to track table width
    const [isTableConstrained, setIsTableConstrained] = useState(false);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    // Monitor table container width
    useEffect(() => {
        const checkTableWidth = () => {
            if (tableContainerRef.current) {
                // If table width is less than 1100px, consider it constrained
                // Lower threshold to 850px to hide priority text earlier
                setIsTableConstrained(
                    tableContainerRef.current.offsetWidth < 850,
                );
            }
        };

        checkTableWidth();
        window.addEventListener('resize', checkTableWidth);
        return () => window.removeEventListener('resize', checkTableWidth);
    }, []);

    const tabNames = [
        { name: 'Check Queue', count: queue?.check?.length || 0, tab: '1' },
        { name: 'ACH Queue', count: queue?.ach?.length || 0, tab: '2' },
    ];

    const columnHelper = createColumnHelper<
        FundRequest & { action?: string }
    >();

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['fundrequest', filters, status],
        queryFn: async () => {
            // Make sure status is included in the request
            const data = await fetchFundRequests({
                ...filters,
                status,
            });
            if (status === 'ap_review') {
                loadQueue();
            }
            return data;
        },
    });

    // Fetch status counts once when component mounts
    useEffect(() => {
        const loadStatusCounts = async () => {
            try {
                const countsData = await fetchFundRequestStatusCounts();
                setStatusCounts(countsData);
            } catch (error) {
                console.error('Error fetching status counts:', error);
            }
        };

        loadStatusCounts();
    }, []);

    useEffect(() => {
        if (data) {
            setResults(data.results);
            setPages(Math.ceil(data.count / pageSize));
        }
    }, [data]);

    useEffect(() => {
        // The only searchParams that should be updated are the status.
        // On status changes, reset the filters
        const status = searchParams.get('status');
        if (status) {
            console.log('setting status to : ', status);
            setStatus(status);
            const newFilters = { ...filters, status };
            console.log('newFilters: ', newFilters);
            setFilters(newFilters);
            // Reset coordinator selection when status changes
            setSelectedCoordinator(null);
            if (vendorRef.current) {
                vendorRef.current.clearValue();
            }
        }
    }, [searchParams]);

    // Track if user has been initialized to prevent repeated calls
    const [userInitialized, setUserInitialized] = useState(false);

    // Check if user has privileged roles that should skip coordinator filter
    const hasPrivilegedRole = useCallback(() => {
        const privilegedRoles = [
            'ap coordinator',
            'director of finance',
            'administrator',
        ];
        const matches = (value?: string) =>
            !!value && privilegedRoles.includes(value.toLowerCase());

        if ((user?.roles || []).some(matches)) {
            return true;
        }

        if ((person?.groups || []).some((group) => matches(group.name))) {
            return true;
        }

        if ((person?.roles || []).some(matches)) {
            return true;
        }

        return false;
    }, [user?.roles, person?.groups, person?.roles]);

    // Refresh user data when component mounts (only once)
    useEffect(() => {
        if (!userInitialized) {
            const initializeUser = async () => {
                try {
                    await refreshUser();
                    setUserInitialized(true);
                } catch (error) {
                    console.error('Error refreshing user:', error);
                    setUserInitialized(true); // Set to true even on error to prevent retry loops
                }
            };

            initializeUser();
        }
    }, [refreshUser, userInitialized]);

    // Memoize loadFinancialCoordinators to prevent unnecessary re-renders
    const loadFinancialCoordinators = useCallback(async () => {
        const response = await fetchFinancialCoordinators();
        const mappedCoordinators = (response.results ?? [])?.map(
            (coordinator: SearchFinancialCoordinatorType) => ({
                value: coordinator?.id.toString(),
                label: coordinator?.first_name + ' ' + coordinator.last_name,
            }),
        );

        setFinancialCoordinators(mappedCoordinators);

        // Check if current user's person ID exists in coordinator options and preselect it
        // Skip preselection if user has privileged roles (AP Coordinator or Director of Finance)
        if (person?.id && !hasPrivilegedRole()) {
            const currentUserOption = mappedCoordinators.find(
                (coordinator: SelectOption) =>
                    coordinator.value === person.id.toString(),
            );

            if (currentUserOption) {
                // Set the selected coordinator state
                setSelectedCoordinator(currentUserOption);

                // Set the filter to include the current user's coordinator
                // Use functional update to ensure we have the latest filters
                setFilters((prev) => ({
                    ...prev,
                    coordinator: person.id.toString(),
                    status: status, // Ensure status is preserved
                }));
            }
        }
    }, [person?.id, hasPrivilegedRole, status]);

    // Load coordinators and preselect current user after status is set (unless they have privileged roles)
    useEffect(() => {
        if (person?.id && status && userInitialized) {
            loadFinancialCoordinators();
        }
    }, [person?.id, status, userInitialized, loadFinancialCoordinators]);

    const columns = [
        // Priority column (flag icon)
        columnHelper.accessor('priority', {
            header: () => '',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div
                        className='cursor-pointer rounded p-1.5 transition-all duration-200 hover:bg-primary/10 active:scale-95 active:bg-primary/20'
                        onClick={() => handleSort('priority')}
                        style={{ marginLeft: '5px' }}
                        title='Sort by priority'
                    >
                        <span className='flex items-center'>
                            {row.fund_request_status.value === 'Submitted' ||
                            row.priority === 'high' ? (
                                <MdFlag
                                    size={20}
                                    className='shrink-0 text-red'
                                />
                            ) : row.priority === 'low' ? (
                                <MdFlag
                                    size={20}
                                    className='shrink-0 text-[#AFC12D]'
                                />
                            ) : (
                                <MdFlag
                                    size={20}
                                    className='shrink-0 text-yellow'
                                />
                            )}
                        </span>
                    </div>
                );
            },
        }),
        // ID column
        columnHelper.accessor('id', {
            header: () => 'ID',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div
                        className='whitespace-nowrap rounded text-center font-semibold text-primary transition-all duration-200 hover:bg-primary/10 active:scale-95 active:bg-primary/20'
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            navigate(`/fund-requests/${row.id}`);
                        }}
                    >
                        {props.row.original.id}
                    </div>
                );
            },
        }),
        // Fund Name column
        columnHelper.accessor('fund_name', {
            header: () => 'Fund Name',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div className='w-full'>
                        <div className='flex items-center gap-[6px]'>
                            <span
                                className='font-semibold'
                                title={row.fund_name}
                            >
                                {isTableConstrained
                                    ? shortenName(row.fund_name)
                                    : row.fund_name}
                            </span>
                            {row.direct_vendor_pay_only && (
                                <span
                                    title='Direct Vendor Pay Only'
                                    aria-label='Direct Vendor Pay Only'
                                    className='inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-yellow text-[12px]/[18px] font-bold text-white'
                                >
                                    !
                                </span>
                            )}
                        </div>
                        <div
                            className='text-secondary'
                            style={{ fontSize: 'small' }}
                        >
                            {row.ntaf_id}
                        </div>
                    </div>
                );
            },
        }),
        // Vendor column
        columnHelper.accessor('vendor', {
            header: () => 'Vendor',
            cell: (props) => {
                const row = props.row.original;
                const vendor = row.vendor;

                if (!vendor) {
                    return null;
                }

                return (
                    <div className='w-full'>
                        <div title={vendor.name}>
                            {isTableConstrained
                                ? shortenName(vendor.name)
                                : vendor.name}
                        </div>
                        <div
                            className='text-secondary'
                            style={{ fontSize: 'small' }}
                        >
                            {vendor.id}
                        </div>
                    </div>
                );
            },
        }),
        // Coordinator column
        columnHelper.accessor('financial_coordinator', {
            header: () => 'Coordinator',
            cell: (props) => {
                const fc = props.getValue();
                if (!fc) return '';

                const fullName = `${fc?.first_name} ${fc?.last_name}`;
                const shortName = `${fc?.first_name?.[0] || ''}. ${
                    fc?.last_name || ''
                }`;

                return (
                    <div className='w-full' title={fullName}>
                        {isTableConstrained ? shortName : fullName}
                    </div>
                );
            },
        }),
        columnHelper.accessor('fund_request_status', {
            header: () => 'Status',
            cell: (props) => {
                const statusValue = props.getValue().value;
                const statusLabel = props.renderValue()?.label;

                return (
                    <div
                        className={`inline-flex rounded-full px-3 py-1 text-[12px]/[14.4px] hover:bg-opacity-90 ${getStatusStyle(
                            statusValue,
                        )}`}
                    >
                        {statusLabel === 'Needs Additional Info'
                            ? 'Needs Info'
                            : statusLabel}
                    </div>
                );
            },
        }),
        columnHelper.accessor('submitted_date', {
            header: () => 'Submitted',
            cell: (props) => {
                const value = props.getValue();
                return value ? moment(value).format('MM/DD/YYYY') : '';
            },
        }),
        columnHelper.accessor('updated_date', {
            header: () => 'Last Updated',
            cell: (props) => {
                return (
                    <>
                        {moment(props.renderValue()).format('MM/DD/YYYY')}
                        {/* <div className='text-[12px]/[16px] text-[#949599]'>
                            Changed to In Review
                        </div> */}
                    </>
                );
            },
        }),
        columnHelper.accessor('item_count', {
            header: () => 'Items',
            cell: (props) => {
                return <div className='text-center'>{props.getValue()}</div>;
            },
        }),
        columnHelper.accessor('unread_notes', {
            header: () => 'Unread Notes',
            cell: (props) => {
                const count = props.getValue() as number | undefined;
                const display =
                    typeof count === 'number' && count > 0 ? count : '';
                return (
                    <div className='text-center'>
                        <span
                            className={
                                display ? 'text-red-500 font-semibold' : ''
                            }
                        >
                            {display}
                        </span>
                    </div>
                );
            },
        }),
        columnHelper.accessor('request_amount', {
            header: () => 'Amount',
            cell: (props) => {
                const value = props.getValue();
                // Ensure value is a number
                const numericValue =
                    typeof value === 'string' ? parseFloat(value) : value;
                return (
                    <div className='text-center'>
                        {numericValue ? formatCurrency(numericValue) : ''}
                    </div>
                );
            },
        }),
        columnHelper.accessor('action', {
            header: () => 'Actions',
            cell: (props) => {
                const { id, fund_request_status } = props.row.original;
                const isAdminDraft =
                    fund_request_status?.value === 'admin_draft';

                const handleDeleteClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setFundRequestToDelete(id);
                    setDeleteModalOpen(true);
                };

                return (
                    <div className='flex items-center justify-center gap-3'>
                        <button
                            onClick={() => {
                                navigate(`/fund-requests/${id}`);
                            }}
                            title='View'
                        >
                            <FiEye className='text-primary' />
                        </button>
                        <Link
                            to={{
                                pathname: `/fund-requests/${id}/pdf`,
                            }}
                            target='_blank'
                            title='Download PDF'
                        >
                            <MdOutlineFileDownload className='text-primary' />
                        </Link>
                        {isAdminDraft && (
                            <button
                                onClick={handleDeleteClick}
                                title='Delete'
                                className='hover:text-red-700 text-danger'
                            >
                                <FaTrash />
                            </button>
                        )}
                    </div>
                );
            },
        }),
    ];

    const table = useReactTable<FundRequest>({
        data: results,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleSort = (columnId: string) => {
        const mappedValue = sortByMap[columnId] || columnId;

        // Prepare the new filter state
        const newFilters = { ...filters };

        // Toggle sort direction if already sorting by this column
        if (filters.sort_by === mappedValue) {
            newFilters.sort = filters.sort === Sort.ASC ? Sort.DESC : Sort.ASC;
        } else {
            // Set new sort column and default to ascending
            newFilters.sort_by = mappedValue;
            newFilters.sort = Sort.ASC;
        }

        // Always ensure status filter is maintained when sorting
        newFilters.status = status;
        newFilters.page = 1; // Reset to first page when sorting

        // Apply all filter changes at once
        setFilters(newFilters);
    };

    const loadVendor = async (search: string) => {
        if (!search) return [];
        const response = await fetchVendors(search);
        const mappedVendors = (response.results ?? [])?.map(
            (vendor: SearchVendorType) => ({
                value: vendor?.id,
                label: vendor?.name,
            }),
        );

        return mappedVendors;
    };

    const loadPatients = async (search: string) => {
        if (!search) return [];
        const response = await fetchPatients(search);
        const mappedVendors = (response.results ?? [])?.map(
            (vendor: SearchVendorType) => ({
                value: vendor?.id,
                label: vendor?.name,
            }),
        );

        return mappedVendors;
    };

    const loadQueue = async () => {
        const response = await fetchQueue();
        setQueue(response);
    };

    // Handle fund request deletion
    const handleConfirmDelete = async () => {
        if (!fundRequestToDelete) return;

        setIsDeleting(true);
        try {
            await deleteFundRequest(fundRequestToDelete);
            // Refetch the data after successful deletion
            await refetch();
            // Close the modal and reset state
            setDeleteModalOpen(false);
            setFundRequestToDelete(null);
        } catch (error) {
            console.error('Error deleting fund request:', error);
            alert(
                error instanceof Error
                    ? error.message
                    : 'Error deleting fund request',
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const renderSortIcon = (columnId: string) => {
        const mappedValue = sortByMap[columnId] || columnId;
        if (filters.sort_by === mappedValue) {
            return filters.sort === Sort.ASC ? (
                <FaSortUp className='ml-1 inline-block text-primary' />
            ) : (
                <FaSortDown className='ml-1 inline-block text-primary' />
            );
        }
        return null;
    };

    return (
        <>
            <Breadcrumb pageName={generatePageName(status)} />
            <FundRequestStatusTabs
                setStatus={(newStatus: string) =>
                    setSearchParams({ status: newStatus })
                }
                status={status}
                statusTabs={statusTabs}
                statusCounts={statusCounts}
            />

            <div
                className={
                    status === 'ap_review'
                        ? 'mx-auto grid grid-cols-12 gap-7.5'
                        : 'mx-auto'
                }
                style={{ maxWidth: '1920px' }}
            >
                <div
                    className={
                        status === 'ap_review'
                            ? 'col-span-12 xl:col-span-8'
                            : ''
                    }
                >
                    <section
                        className='data-table-common mx-auto rounded-sm border border-stroke bg-white pb-4 shadow-default dark:border-strokedark dark:bg-boxdark'
                        style={{ maxWidth: '1920px' }}
                    >
                        {status !== 'appealed' && (
                            <div className='border-b border-stroke px-6 py-4'>
                                <div className='flex flex-wrap items-center gap-4'>
                                    <div className='w-full min-w-[125px] max-w-[175px]'>
                                        <input
                                            type='text'
                                            inputMode='numeric'
                                            pattern='[0-9]*'
                                            placeholder='Fund Request ID'
                                            className='w-full rounded border border-stroke px-3 py-2 outline-none focus:border-primary'
                                            value={filters.fund_request_id}
                                            onChange={(e) => {
                                                const numericValue =
                                                    e.currentTarget.value.replace(
                                                        /[^0-9]/g,
                                                        '',
                                                    );
                                                setFilters({
                                                    ...filters,
                                                    fund_request_id:
                                                        numericValue,
                                                    page: 1,
                                                });
                                            }}
                                        />
                                    </div>
                                    <div className='w-full min-w-[125px] max-w-[175px]'>
                                        <Select
                                            ref={coordinatorRef}
                                            menuPlacement='auto'
                                            placeholder='Coordinator'
                                            className='w-full'
                                            isClearable
                                            options={financialCoordinators}
                                            value={selectedCoordinator}
                                            onChange={(e) => {
                                                setSelectedCoordinator(e);
                                                if (e?.value) {
                                                    setFilters({
                                                        ...filters,
                                                        coordinator: e.value,
                                                    });
                                                } else {
                                                    setFilters({
                                                        ...filters,
                                                        coordinator: null,
                                                    });
                                                }
                                            }}
                                            components={{
                                                IndicatorSeparator: null,
                                            }}
                                            styles={colorStyles}
                                        />
                                    </div>
                                    <div className='w-full min-w-[125px] max-w-[175px]'>
                                        <AsyncSelect
                                            placeholder='Fund Name/NTAFID'
                                            cacheOptions
                                            loadOptions={loadPatients}
                                            className='w-full'
                                            isSearchable
                                            // value={selectedPatient}
                                            isClearable
                                            onChange={(
                                                newValue: SingleValue<SelectOption>,
                                            ) => {
                                                if (
                                                    newValue &&
                                                    newValue.value
                                                ) {
                                                    setFilters({
                                                        ...filters,
                                                        patient_id:
                                                            newValue.value,
                                                    });
                                                } else {
                                                    setFilters({
                                                        ...filters,
                                                        patient_id: null,
                                                    });
                                                }
                                            }}
                                            styles={colorStyles}
                                        />
                                    </div>
                                    <div className='w-full min-w-[125px] max-w-[175px]'>
                                        <AsyncSelect
                                            ref={vendorRef}
                                            placeholder='Vendor'
                                            cacheOptions
                                            loadOptions={loadVendor}
                                            className='w-full'
                                            isSearchable
                                            // value={vendorId}
                                            isClearable={true}
                                            onChange={(vendorOption) => {
                                                if (vendorOption) {
                                                    setFilters({
                                                        ...filters,
                                                        vendor_id:
                                                            vendorOption.value,
                                                    });
                                                } else {
                                                    setFilters({
                                                        ...filters,
                                                        vendor_id: null,
                                                    });
                                                }
                                            }}
                                            styles={colorStyles}
                                        />
                                    </div>
                                    <input
                                        type='date'
                                        name='created_date_start'
                                        className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                                        value={filters.created_date_start}
                                        onChange={(e) => {
                                            setFilters({
                                                ...filters,
                                                created_date_start:
                                                    e.currentTarget.value,
                                            });
                                        }}
                                    />
                                    to
                                    <input
                                        type='date'
                                        name='created_date_end'
                                        className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                                        value={filters.created_date_end}
                                        onChange={(e) => {
                                            setFilters({
                                                ...filters,
                                                created_date_end:
                                                    e.currentTarget.value,
                                            });
                                        }}
                                    />
                                    {status !== 'ap_review' && (
                                        <div className='ml-auto'>
                                            <Link
                                                onClick={() =>
                                                    setIsCreatedFundRequestShowing(
                                                        true,
                                                    )
                                                }
                                                to='#'
                                                className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                            >
                                                <FaPlus />
                                                Add Request
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div
                            className='flex items-center justify-between px-8 py-1'
                            style={{ fontSize: '12px' }}
                        >
                            <p>
                                Showing{' '}
                                {Math.min(
                                    pageSize,
                                    (data?.count || 0) - (page - 1) * pageSize,
                                )}{' '}
                                of {data?.count || 0} results
                            </p>

                            <div className='flex items-center gap-5'>
                                <div className='inline-flex items-center justify-center gap-1.5'>
                                    <FiEye className='text-primary' size={18} />
                                    View
                                </div>
                                <div className='inline-flex items-center justify-center gap-1.5'>
                                    <MdOutlineFileDownload
                                        className='text-primary'
                                        size={18}
                                    />
                                    Download
                                </div>
                            </div>
                        </div>

                        <div
                            className='overflow-x-auto'
                            ref={tableContainerRef}
                            style={{ minWidth: '100%' }}
                        >
                            <table
                                className='datatable-table datatable-one w-full table-fixed border-collapse break-words px-4 md:px-8'
                                style={{ tableLayout: 'fixed' }}
                            >
                                <thead className='border-separate px-4'>
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => (
                                            <tr
                                                className='border-t border-stroke bg-whiten'
                                                style={{ fontSize: '14px' }}
                                                key={headerGroup.id}
                                            >
                                                {headerGroup.headers.map(
                                                    (header) => (
                                                        <th
                                                            className='px-3'
                                                            style={{
                                                                paddingTop:
                                                                    '16px',
                                                                paddingBottom:
                                                                    '16px',
                                                                paddingLeft:
                                                                    '12px',
                                                                paddingRight:
                                                                    '12px',
                                                                borderBottom:
                                                                    filters.sort_by ===
                                                                    header
                                                                        .column
                                                                        .id
                                                                        ? '1px solid #01aeb4'
                                                                        : undefined,
                                                                width:
                                                                    header
                                                                        .column
                                                                        .id ===
                                                                    'priority'
                                                                        ? '5%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'id'
                                                                        ? '8%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'fund_name'
                                                                        ? '13%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'vendor'
                                                                        ? '11%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'financial_coordinator'
                                                                        ? '9%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'fund_request_status'
                                                                        ? '9%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'submitted_date'
                                                                        ? '9%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'updated_date'
                                                                        ? '9%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'item_count'
                                                                        ? '6%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'unread_notes'
                                                                        ? '10%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'request_amount'
                                                                        ? '11%'
                                                                        : header
                                                                              .column
                                                                              .id ===
                                                                          'action'
                                                                        ? '10%'
                                                                        : 'auto',
                                                            }}
                                                            key={header.id}
                                                        >
                                                            <div
                                                                className={`flex items-center ${
                                                                    [
                                                                        'item_count',
                                                                        'unread_notes',
                                                                        'request_amount',
                                                                        'action',
                                                                    ].includes(
                                                                        header
                                                                            .column
                                                                            .id,
                                                                    )
                                                                        ? 'justify-center'
                                                                        : 'justify-center'
                                                                } cursor-pointer rounded p-2 transition-all duration-200 hover:bg-primary/10 active:scale-95 active:bg-primary/20 ${
                                                                    filters.sort_by ===
                                                                    header
                                                                        .column
                                                                        .id
                                                                        ? 'text-primary'
                                                                        : ''
                                                                }`}
                                                                onClick={() =>
                                                                    handleSort(
                                                                        header
                                                                            .column
                                                                            .id,
                                                                    )
                                                                }
                                                            >
                                                                {header.column
                                                                    .id ===
                                                                'priority' ? (
                                                                    <MdOutlineFlag
                                                                        size={
                                                                            20
                                                                        }
                                                                        className='mr-1 shrink-0'
                                                                        title='Priority'
                                                                    />
                                                                ) : null}

                                                                <span
                                                                    className={`font-semibold ${
                                                                        filters.sort_by ===
                                                                        header
                                                                            .column
                                                                            .id
                                                                            ? 'text-primary'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    {header.isPlaceholder
                                                                        ? null
                                                                        : flexRender(
                                                                              header
                                                                                  .column
                                                                                  .columnDef
                                                                                  .header,
                                                                              header.getContext(),
                                                                          )}
                                                                </span>

                                                                {renderSortIcon(
                                                                    header
                                                                        .column
                                                                        .id,
                                                                )}
                                                            </div>
                                                        </th>
                                                    ),
                                                )}
                                            </tr>
                                        ))}
                                </thead>
                                <tbody>
                                    {isLoading
                                        ? Array.from({ length: 10 }).map(
                                              (_, index) => (
                                                  <tr key={index}>
                                                      {columns.map(
                                                          (
                                                              _column,
                                                              colIndex,
                                                          ) => (
                                                              <td
                                                                  key={colIndex}
                                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                                              >
                                                                  <div className='max-w-sm animate-pulse'>
                                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                                  </div>
                                                              </td>
                                                          ),
                                                      )}
                                                      <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                                          <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                              <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                          </div>
                                                      </td>
                                                  </tr>
                                              ),
                                          )
                                        : table
                                              .getRowModel()
                                              .rows.map((row) => {
                                                  return (
                                                      <tr key={row.id}>
                                                          {row
                                                              .getVisibleCells()
                                                              .map((cell) => (
                                                                  <td
                                                                      className='px-3'
                                                                      style={{
                                                                          paddingTop:
                                                                              '16px',
                                                                          paddingBottom:
                                                                              '16px',
                                                                          paddingLeft:
                                                                              '12px',
                                                                          paddingRight:
                                                                              '12px',
                                                                          width:
                                                                              cell
                                                                                  .column
                                                                                  .id ===
                                                                              'priority'
                                                                                  ? '5%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'id'
                                                                                  ? '8%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'fund_name'
                                                                                  ? '13%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'vendor'
                                                                                  ? '11%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'financial_coordinator'
                                                                                  ? '9%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'fund_request_status'
                                                                                  ? '9%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'submitted_date'
                                                                                  ? '9%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'updated_date'
                                                                                  ? '9%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'item_count'
                                                                                  ? '6%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'unread_notes'
                                                                                  ? '10%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'request_amount'
                                                                                  ? '11%'
                                                                                  : cell
                                                                                        .column
                                                                                        .id ===
                                                                                    'action'
                                                                                  ? '10%'
                                                                                  : 'auto',
                                                                      }}
                                                                      key={
                                                                          cell.id
                                                                      }
                                                                  >
                                                                      {flexRender(
                                                                          cell
                                                                              .column
                                                                              .columnDef
                                                                              .cell,
                                                                          cell.getContext(),
                                                                      )}
                                                                  </td>
                                                              ))}
                                                      </tr>
                                                  );
                                              })}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            page={page}
                            pages={pages}
                            setPage={(val: number) => {
                                setPage(val);
                                setFilters({ ...filters, page: val });
                            }}
                            totalCount={data?.count}
                        />
                    </section>
                </div>

                {status === 'ap_review' && (
                    <div className='col-span-12 xl:col-span-4'>
                        <Panel addClasses={'bg-white border-stroke !p-4'}>
                            <Tabs
                                openTab={openTab}
                                setOpenTab={setOpenTab}
                                tabNames={tabNames}
                                className='!mb-2 justify-around'
                                tabClassName='!px-8'
                            >
                                {openTab === 1 && (
                                    <QueueTable
                                        queue={queue?.check}
                                        paymentType='check'
                                        refetch={refetch}
                                    />
                                )}
                                {openTab === 2 && (
                                    <QueueTable
                                        queue={queue?.ach}
                                        paymentType='ach'
                                        refetch={refetch}
                                    />
                                )}
                            </Tabs>
                        </Panel>
                    </div>
                )}
            </div>

            <AddFundRequestDrawer
                isOpen={isCreateFundRequestShowing}
                setIsOpen={setIsCreatedFundRequestShowing}
            />

            <DeleteFundRequestModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setFundRequestToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
                fundRequestId={fundRequestToDelete || 0}
            />
        </>
    );
};

export default FundRequests;
